import React, { useState, useEffect, useContext, useRef } from "react"
import moment from "moment"

import { FirebaseContext } from "./Firebase"
import "firebase/firestore"
import "firebase/analytics"
import "firebase/functions"
import LineThumb from "./LineThumb"
import ProfileThumb from "./ProfileThumb"
import LikeButton from "./LikeButton"
import arrow from "../../images/arrow.svg"
import Search from "../tracks/Search"
import { Dialog, Transition } from "@headlessui/react"
import { Fragment } from "react"
import { createFreeManualRacingLine } from "../../utils/manualLineCreation"

export default (props) => {
    const auth = useContext(FirebaseContext)
    const firebase = auth.app
    const linesDb = firebase.firestore().collection(`lines`)
    const [lines, setLines] = useState(null)
    const [cursor, setCursor] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const loadMoreRef = useRef(null)
    const observerRef = useRef(null)
    const seenNamesRef = useRef(new Set())
    const [showTrackPicker, setShowTrackPicker] = useState(false)
    const [creating, setCreating] = useState(false)
    const [createError, setCreateError] = useState(null)
    const {
        wrapperClass = "post-feed horizontal-feed",
        publicLinesOnly = true,
        closedLinesOnly = true,
        oneRacingLinePerTrack = true,
        infiniteScroll = false,
        showProfilePhoto = true,
        showVisibility = false,
        showLike = false,
        alwaysShowNewLine = false,
        filterTrack,
        filterUser,
        limit = 5,
        onNewLine = () => (location.href = "/racing-line-editor"),
        setLine,
    } = props

    useEffect(() => {
        // Reset when filters change
        setLines(null)
        setCursor(null)
        setHasMore(true)
        seenNamesRef.current = new Set()
        load(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filterTrack,
        filterUser,
        publicLinesOnly,
        closedLinesOnly,
        oneRacingLinePerTrack,
        limit,
        infiniteScroll,
    ])

    const buildBaseQuery = () => {
        let query = linesDb
        if (publicLinesOnly)
            query = addWhereToQuery(query, ["visibility", "==", "public"])
        if (closedLinesOnly)
            query = addWhereToQuery(query, ["isCloseDetected", "==", true])
        if (filterUser)
            query = addWhereToQuery(query, ["user", "==", filterUser])
        if (filterTrack)
            query = addWhereToQuery(query, ["name", "==", filterTrack])
        return query.orderBy("created", "desc")
    }

    const load = async (initial = false) => {
        try {
            if (
                !initial &&
                (!infiniteScroll || !hasMore || isLoadingMore || isLoading)
            )
                return
            if (initial && isLoading) return

            const q = buildBaseQuery()
            const pageSize = +limit
            let paged = q.limit(pageSize)
            if (!initial && cursor) paged = q.startAfter(cursor).limit(pageSize)

            initial ? setIsLoading(true) : setIsLoadingMore(true)
            const linesDbRes = await paged.get()

            let lines = linesDbRes.docs.map((doc) => {
                return { id: doc.id, ...doc.data() }
            })

            if (oneRacingLinePerTrack) {
                if (infiniteScroll) {
                    lines = lines.filter((item) => {
                        const n = (item && item.name) || ""
                        if (seenNamesRef.current.has(n)) return false
                        seenNamesRef.current.add(n)
                        return true
                    })
                } else {
                    lines = lines.filter(
                        (value, index, array) =>
                            index === 0 || value.name != array[index - 1].name
                    )
                }
            }

            if (initial) {
                setLines(lines)
            } else {
                setLines((prev) => (prev ? [...prev, ...lines] : lines))
            }

            const last = linesDbRes.docs[linesDbRes.docs.length - 1]
            if (infiniteScroll) {
                if (last) setCursor(last)
                setHasMore(linesDbRes.docs.length === pageSize)
            } else {
                setHasMore(false)
            }
            console.log("Lines loaded: ", lines)
        } catch (error) {
            console.error("Error loading document: ", error)
        } finally {
            setIsLoading(false)
            setIsLoadingMore(false)
        }
    }

    const addWhereToQuery = (query, filter) => {
        if (filter && filter.length == 3) {
            return query.where(...filter)
        }
    }

    const onClick = (e, line) => {
        if (typeof setLine !== "function") return

        setLine(line)
        e.preventDefault()
    }

    // Determine if current page has a track context; if not, we will show a picker
    const hasTrackContext = Boolean(filterTrack)
    // Use latLng already provided upstream if available
    const latLng = props.latLng

    // IntersectionObserver to trigger load more
    useEffect(() => {
        if (!infiniteScroll) return
        if (!loadMoreRef.current) return
        if (observerRef.current) observerRef.current.disconnect()
        observerRef.current = new IntersectionObserver(
            (entries) => {
                const first = entries[0]
                if (first && first.isIntersecting) {
                    load(false)
                }
            },
            { root: null, rootMargin: "1000px", threshold: 0 }
        )
        observerRef.current.observe(loadMoreRef.current)
        return () => {
            if (observerRef.current) observerRef.current.disconnect()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [infiniteScroll, loadMoreRef.current, cursor, hasMore, isLoading])

    // Window scroll fallback in case IntersectionObserver doesn't fire
    useEffect(() => {
        if (!infiniteScroll) return
        let ticking = false
        const onScroll = () => {
            if (ticking) return
            ticking = true
            window.requestAnimationFrame(() => {
                const nearBottom =
                    window.innerHeight + window.scrollY >=
                    document.body.offsetHeight - 800
                if (nearBottom) load(false)
                ticking = false
            })
        }
        window.addEventListener("scroll", onScroll, { passive: true })
        return () => window.removeEventListener("scroll", onScroll)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [infiniteScroll, cursor, hasMore, isLoading, isLoadingMore])

    return (
        <div className="space-y-8 mt-10">
            {/* Action Buttons Section */}
            <div className="flex sm:flex-row flex-col w-full gap-4 justify-center items-stretch sm:items-center relative px-4 sm:px-0 max-w-5xl mx-auto">
                {/* Primary Draw Racing Line Button */}
                <button
                    onClick={(e) => {
                        if (!hasTrackContext) {
                            e.preventDefault()
                            setShowTrackPicker(true)
                            return
                        }
                        onNewLine(e)
                    }}
                    className="flex items-center justify-center gap-3 px-6 sm:px-8 py-4 bg-[#5159a2] !text-white rounded-xl hover:bg-[#424d8a] transition-all duration-200 shadow-md hover:shadow-lg"
                >
                    <img
                        src={arrow}
                        alt="Arrow logo"
                        className="!w-5 !h-5 !my-0 !mx-0 !p-0 brightness-0 invert opacity-90"
                    />
                    <span className="text-base sm:text-lg font-semibold whitespace-nowrap uppercase font-racing">
                        Draw Racing Line
                    </span>
                </button>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                    <span className="text-gray-400 hidden sm:block text-sm">
                        or
                    </span>
                    <div className="flex flex-col sm:flex-row items-stretch gap-3">
                        <a
                            href="/iracing/"
                            className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 bg-white border border-gray-300 !text-gray-700 hover:!text-gray-900 no-underline rounded-xl hover:border-gray-400 hover:shadow-md transition-all duration-200"
                            aria-label="Learn about iRacing analysis and import a lap"
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="flex-shrink-0 sm:w-5 sm:h-5"
                                aria-hidden="true"
                            >
                                <path
                                    d="M12 3L20 7.5V16.5L12 21L4 16.5V7.5L12 3Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M12 12L20 7.5M12 12V21M12 12L4 7.5"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <div className="flex flex-col items-start leading-tight">
                                <span className="text-xs uppercase text-gray-500 font-medium">
                                    Analyze
                                </span>
                                <span className="text-sm sm:text-base font-semibold">
                                    iRacing Lap
                                </span>
                            </div>
                        </a>
                        <a
                            href="/gopro-racing-line-importer/"
                            className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 bg-white border border-gray-300 !text-gray-700 hover:!text-gray-900 no-underline rounded-xl hover:border-gray-400 hover:shadow-md transition-all duration-200"
                            aria-label="Learn about GoPro analysis and import a video"
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="flex-shrink-0 sm:w-5 sm:h-5"
                                aria-hidden="true"
                            >
                                <rect
                                    x="2"
                                    y="6"
                                    width="20"
                                    height="12"
                                    rx="2"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                />
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="3"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M9 2L7 6M15 2L17 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="flex flex-col items-start leading-tight">
                                <span className="text-xs uppercase text-gray-500 font-medium">
                                    Analyze
                                </span>
                                <span className="text-sm sm:text-base font-semibold">
                                    GoPro Video
                                </span>
                            </div>
                        </a>
                        <a
                            href="/alfano-racing-line-importer/"
                            className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 bg-white border border-gray-300 !text-gray-700 hover:!text-gray-900 no-underline rounded-xl hover:border-gray-400 hover:shadow-md transition-all duration-200"
                            aria-label="Learn about Alfano analysis and import logger data"
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="flex-shrink-0 sm:w-5 sm:h-5"
                                aria-hidden="true"
                            >
                                <path
                                    d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <div className="flex flex-col items-start leading-tight">
                                <span className="text-xs uppercase text-gray-500 font-medium">
                                    Analyze
                                </span>
                                <span className="text-sm sm:text-base font-semibold">
                                    Alfano Data
                                </span>
                            </div>
                        </a>
                    </div>
                </div>
            </div>

            {/* Racing Lines Grid */}
            <div className={wrapperClass}>
                {lines &&
                    lines.map((line) => (
                        <article
                            key={line.id}
                            className="post-card track-card hover:shadow-lg transition-shadow"
                        >
                            <div className="post-card-content track-card-content">
                                <a
                                    className="track-card-image flex items-center justify-center w-full"
                                    href={
                                        line.url === "/r"
                                            ? `/r/${line.id}`
                                            : `${line.url}/r/${line.id}`
                                    }
                                    onClick={(e) => onClick(e, line)}
                                >
                                    <LineThumb
                                        points={line.points}
                                        width={200}
                                        height={130}
                                    ></LineThumb>
                                </a>
                                <div className="p-4">
                                    <header className="post-card-header track-card-header">
                                        {!filterTrack && (
                                            <h2 className="post-card-title track-card-title font-bold text-lg">
                                                {line.name}
                                            </h2>
                                        )}
                                        <div className="text-sm text-gray-600">
                                            {line.vehicle &&
                                                line.vehicle != "" && (
                                                    <span>
                                                        {line.vehicle} ·{" "}
                                                    </span>
                                                )}
                                            <span>
                                                {moment(
                                                    line.created.toDate()
                                                ).format("MMM, YYYY")}
                                            </span>
                                            {showVisibility && (
                                                <span className="ml-2 px-2 py-1 bg-gray-100 rounded-full text-xs">
                                                    {line.visibility}
                                                </span>
                                            )}
                                        </div>
                                    </header>
                                    <footer className="post-card-meta mt-4 flex items-center justify-between">
                                        {showProfilePhoto && (
                                            <ProfileThumb uid={line.user} />
                                        )}
                                        {showLike && (
                                            <LikeButton lineId={line.id} />
                                        )}
                                    </footer>
                                </div>
                            </div>
                        </article>
                    ))}
            </div>

            {/* Infinite scroll sentinel */}
            {infiniteScroll && (
                <>
                    {/* Infinite scroll sentinel */}
                    <div ref={loadMoreRef} className="h-1 w-full" />
                    {isLoadingMore && (
                        <div className="text-center text-gray-500 py-4">
                            Loading more…
                        </div>
                    )}
                </>
            )}

            {/* Track Picker Modal */}
            <Transition appear show={showTrackPicker} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-[9998]"
                    onClose={() => setShowTrackPicker(false)}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <div className="relative mx-auto max-w-4xl w-full bg-white rounded-2xl shadow-lg p-0">
                                    <div className="flex items-center justify-between px-5 py-3 border-b">
                                        <Dialog.Title className="text-lg font-semibold">
                                            Select a track near you
                                        </Dialog.Title>
                                        <button
                                            className="text-gray-500 hover:text-gray-700"
                                            onClick={() =>
                                                setShowTrackPicker(false)
                                            }
                                        >
                                            Close
                                        </button>
                                    </div>
                                    <div
                                        className={`p-4 ${
                                            creating ? "animate-pulse" : ""
                                        }`}
                                    >
                                        <Search
                                            latLng={
                                                latLng ||
                                                (() => {
                                                    try {
                                                        const s =
                                                            localStorage.getItem(
                                                                "latLng"
                                                            )
                                                        return s
                                                            ? JSON.parse(s)
                                                            : null
                                                    } catch (_) {
                                                        return null
                                                    }
                                                })()
                                            }
                                            trackCount={10}
                                            defaultFacets={[
                                                "racetrack",
                                                "gokart",
                                            ]}
                                            stickyHeader={false}
                                            selectMode
                                            onSelectTrack={async (hit) => {
                                                setCreateError(null)
                                                setCreating(true)
                                                try {
                                                    const center = Array.isArray(hit.location)
                                                        ? { lat: +hit.location[0], lng: +hit.location[1] }
                                                        : null
                                                    if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) {
                                                        throw new Error("This track does not have a usable map location.")
                                                    }
                                                    try {
                                                        localStorage.setItem("latLng", JSON.stringify(center))
                                                    } catch (_) {}

                                                    const created = await createFreeManualRacingLine({
                                                        auth,
                                                        track: {
                                                            name: hit.name,
                                                            placeId: hit.place_id || hit.placeId || "",
                                                            url: hit.url || "",
                                                            lat: center.lat,
                                                            lng: center.lng,
                                                            zoom: hit.type === "racetrack" ? 15 : 19,
                                                        },
                                                    })
                                                    setShowTrackPicker(false)
                                                    const lineUrl = hit.url
                                                        ? hit.url === "/r"
                                                            ? `/r/${created.lineId}`
                                                            : `${hit.url}/r/${created.lineId}`
                                                        : `/r/${created.lineId}`
                                                    window.location.href = `${lineUrl}?edit=1`
                                                } catch (e) {
                                                    console.error(
                                                        "Track select create error",
                                                        e
                                                    )
                                                    setCreateError(
                                                        e.message ||
                                                            "Failed to create racing line"
                                                    )
                                                } finally {
                                                    setCreating(false)
                                                }
                                            }}
                                        />
                                        {createError && (
                                            <p className="text-sm text-red-600 mt-3">
                                                {createError}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    )
}
